import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Button as BootstrapButton } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faCopy, faSpinner } from "@fortawesome/free-solid-svg-icons";
import { colors } from "utils";
import { ParsedSummary } from "components/pages/Educator/Session/SessionSummaries/ParsedSummary";
import { ErrorBoundary } from "components/shared/ErrorBoundary/ErrorBoundary";
import { Tooltip } from "components/shared/Tooltip/Tooltip";
import { reportSections, sectionKeysOf } from "./exports";
import { renderMarkdown } from "./markdown";
import {
  FilterLabel, GenerateButton, LlmTuningPanel, StaleNote, TokenSummaryBox, TuningFieldFull, TuningHint, TuningSecondaryBtn,
} from "components/shared/LlmTuning/LlmTuning";

const STATE_STYLE = {
  present: { bg: colors.green50, fg: colors.green700, text: "present" },
  empty: { bg: colors.amber50, fg: colors.amber800, text: "empty" },
  fetch_failed: { bg: colors.pink50, fg: colors.pink800, text: "fetch failed" },
  not_available: { bg: colors.neutral100, fg: colors.milkGlass800, text: "n/a" },
};

const formatDate = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

const DocTip = ({ doc }) => (
  <DocBody>
    <strong>{doc.title}</strong>
    {doc.definition && <span>{doc.definition}</span>}
    {doc.source && <span>Source: {doc.source}</span>}
    {doc.other_uses && <span>Also used in: {doc.other_uses}</span>}
  </DocBody>
);

// A packet section title with its doc on hover. It sits inside a <summary>,
// which owns the tab stop, so keyboard users get the doc from DocNote instead.
export const DocTerm = ({ id, doc, label }) => (doc
  ? (
    <Tooltip id={id} tip={<DocTip doc={doc} />} placement="top" align="start" dismissOnEscape>
      <Term>{label}</Term>
    </Tooltip>
  )
  : label);

// The same doc written out inside the expanded section.
export const DocNote = ({ id, doc }) => (doc
  ? <Muted as="div" id={id}><DocTip doc={doc} /></Muted>
  : null);

const GroupTip = () => (
  <DocBody>
    <span><strong>Lesson or standard name:</strong> the session ran under that assignment.</span>
    <span><strong>Quick groups:</strong> run by a quick group.</span>
    <span>
      <strong>Other sessions:</strong> warm-ups, groups with nothing assigned, and sessions before
      Aug 25, 2026 whose assignment could not be recovered.
    </span>
  </DocBody>
);

const COPY_STATUS_MS = 2000;

// Copies the labelled text, never real names: like the exports, the clipboard
// leaves the screen.
export const CopyButton = ({ text, label }) => {
  const [status, setStatus] = useState(null);
  useEffect(() => {
    if (!status) return undefined;
    const timer = setTimeout(() => setStatus(null), COPY_STATUS_MS);
    return () => clearTimeout(timer);
  }, [status]);
  // Without the Clipboard API (plain http) writeText throws instead of rejecting.
  const copy = () => {
    setStatus(null);
    Promise.resolve()
      .then(() => navigator.clipboard.writeText(text))
      .then(() => setStatus("Copied"), () => setStatus("Copy failed"));
  };
  return (
    <CopyRow>
      <LinkButton type="button" aria-label={label} onClick={copy}>
        <FontAwesomeIcon icon={status === "Copied" ? faCheck : faCopy} /> Copy
      </LinkButton>
      <Muted role="status">{status}</Muted>
    </CopyRow>
  );
};

export const ManifestGrid = ({ manifest, docs }) => {
  const keys = sectionKeysOf(manifest);
  const grouped = manifest.some((row) => row.group_label);
  return (
    <>
      <Legend>
        {Object.entries(STATE_STYLE).map(([state, s]) => (
          <StateCell as="span" key={state} $bg={s.bg} $fg={s.fg}>{s.text}</StateCell>
        ))}
        <Muted>Sessions before 2026-08-26 show n/a for pre-computed inputs.</Muted>
      </Legend>
      <ScrollX>
        <Grid>
          <thead>
            <tr>
              <th scope="col">Session</th>
              <th scope="col">Session ID</th>
              <th scope="col">Focus</th>
              {grouped && (
                <th scope="col">
                  <Tooltip id="manifest-group-tip" tip={<GroupTip />} placement="top" align="start" dismissOnEscape>
                    <Term tabIndex={0} aria-describedby="manifest-group-tip">Group</Term>
                  </Tooltip>
                </th>
              )}
              {keys.map((k) => (
                <th scope="col" key={k}>{docs?.[k]?.title || k}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {manifest.map((row) => (
              <tr key={row.session_label}>
                <th scope="row">
                  {row.session_label}
                  <Muted as="div">{formatDate(row.started_at)}</Muted>
                </th>
                <td><SessionId>{row.session_id || "—"}</SessionId></td>
                <td>{row.standard_codes.join(", ") || "—"}</td>
                {grouped && <td>{row.group_label || "—"}</td>}
                {keys.map((key) => {
                  const state = row.sections.find((s) => s.key === key)?.state;
                  const style = STATE_STYLE[state] || STATE_STYLE.not_available;
                  return (
                    <StateCell key={key} $bg={style.bg} $fg={style.fg}>{style.text}</StateCell>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </Grid>
      </ScrollX>
    </>
  );
};

const VIEW_STACKED = "Stacked";
const VIEW_RAW = "Raw";

// Structured output renders through the teacher's ParsedSummary, free-form as
// sanitised Markdown; Raw shows exactly what the model returned.
export const OutputPane = ({ run, h }) => {
  const freeform = run.output_mode === "freeform";
  const [view, setView] = useState(VIEW_STACKED);
  return (
    <Pane>
      <PaneHeader>
        <span>Output — the report this run produced</span>
        <div role="group" aria-label="Output view">
          {[VIEW_STACKED, VIEW_RAW].map((option) => (
            <ViewButton
              key={option}
              size="sm"
              variant={view === option ? "dark" : "outline-secondary"}
              aria-pressed={view === option}
              onClick={() => setView(option)}
            >
              {option === VIEW_STACKED && freeform ? "Rendered" : option}
            </ViewButton>
          ))}
        </div>
      </PaneHeader>
      <PaneBody>
        {view === VIEW_RAW && <RawOutput>{freeform ? run.output_markdown : JSON.stringify(run.output, null, 2)}</RawOutput>}
        {view !== VIEW_RAW && (
          <ErrorBoundary fallback={<Muted>This output could not be rendered; switch to Raw to read it.</Muted>}>
            {freeform && !run.output_markdown?.trim() && <Muted>The model returned no Markdown.</Muted>}
            {freeform && run.output_markdown?.trim() && (
              <MarkdownBody dangerouslySetInnerHTML={{ __html: renderMarkdown(run.output_markdown, h) }} />
            )}
            {!freeform && <ParsedSummary sections={reportSections(run.output, h)} />}
          </ErrorBoundary>
        )}
      </PaneBody>
    </Pane>
  );
};

export const CollapsibleField = ({ label, hint, children }) => (
  <Fold>
    <summary>
      {label}
      {hint && <FoldHint> — {hint}</FoldHint>}
    </summary>
    <FoldBody>{children}</FoldBody>
  </Fold>
);

const OUTPUT_MODES = [["structured", "Structured (schema)"], ["freeform", "Free-form (Markdown)"]];

const promptDefaults = (defaults, mode) => (mode === "freeform"
  ? { system_prompt: defaults.freeform_system_prompt, user_prompt: defaults.freeform_user_prompt }
  : { system_prompt: defaults.system_prompt, user_prompt: defaults.user_prompt });

const toPanel = (v) => ({
  provider: v.provider,
  modelName: v.model_name,
  temperature: Number(v.temperature),
  thinkingLevel: v.thinking_level || "",
  systemPrompt: v.system_prompt,
  userPrompt: v.user_prompt,
});

const fromPanel = {
  provider: "provider",
  modelName: "model_name",
  temperature: "temperature",
  thinkingLevel: "thinking_level",
  systemPrompt: "system_prompt",
  userPrompt: "user_prompt",
};

export const VariantEditor = ({
  variant, sectionKeys, defaults, modelOptions, modelsLoading, tokenRows, onChange, onRemove, onDuplicate, onExport,
  runLabel, running, runDisabled, stale, onRun, namesShown, docs,
}) => {
  const toggleSection = (key) => {
    const current = variant.input_toggles;
    onChange({
      ...variant,
      input_toggles: current.includes(key) ? current.filter((k) => k !== key) : [...current, key],
    });
  };
  const idPrefix = `variant-${variant.label}`;
  const mode = variant.output_mode || "structured";
  const modeDefaults = promptDefaults(defaults, mode);
  // A prompt follows the mode only while it is empty or still that mode's default.
  const setMode = (next) => {
    const to = promptDefaults(defaults, next);
    const swap = (k) => (!variant[k] || variant[k] === modeDefaults[k] ? to[k] : variant[k]);
    onChange({ ...variant, output_mode: next, system_prompt: swap("system_prompt"), user_prompt: swap("user_prompt") });
  };

  return (
    <LlmTuningPanel
      idPrefix={idPrefix}
      title={`Variant ${variant.label}: ${variant.provider} / ${variant.model_name || "default model"}`}
      defaultOpen
      {...toPanel(variant)}
      modelOptions={modelOptions}
      modelsLoading={modelsLoading}
      onChange={(patch) => onChange(Object.entries(patch).reduce(
        (next, [field, value]) => ({ ...next, [fromPanel[field]]: value }), variant,
      ))}
      systemPromptHint={namesShown
        ? "(hide real names to edit; prompts are saved, exported and sent to the model)"
        : "(prefilled with the server default; clear it to use the default)"}
      userPromptHint={namesShown ? "(hide real names to edit)" : "(optional; leave blank to send no task text)"}
      promptsReadOnly={namesShown}
      systemPromptPlaceholder=""
      userPromptPlaceholder="The System Prompt holds the full instructions. Add task text here only if you need it."
      systemPromptRows={10}
      resetDisabled={
        variant.system_prompt === modeDefaults.system_prompt && variant.user_prompt === modeDefaults.user_prompt
      }
      onResetPrompts={() => onChange({ ...variant, ...modeDefaults })}
    >
      <TuningFieldFull as="fieldset">
        <FilterLabel as="legend">
          Output{" "}
          <TuningHint>
            (Switching keeps prompts you edited; Reset prompts loads this mode&apos;s defaults.
            Free-form won&apos;t render in the Figma layout.)
          </TuningHint>
        </FilterLabel>
        <SectionChecks>
          {OUTPUT_MODES.map(([value, label]) => (
            <label key={value} htmlFor={`${idPrefix}-output-${value}`}>
              <input
                id={`${idPrefix}-output-${value}`}
                type="radio"
                name={`${idPrefix}-output-mode`}
                checked={mode === value}
                onChange={() => setMode(value)}
              />
              {label}
            </label>
          ))}
        </SectionChecks>
      </TuningFieldFull>
      <TuningFieldFull as="fieldset">
        <FilterLabel as="legend">
          Input sections <TuningHint>(what this variant sends from the packet)</TuningHint>
        </FilterLabel>
        <SectionChecks>
          {sectionKeys.map((key) => (
            <label key={key} htmlFor={`${idPrefix}-section-${key}`}>
              <input
                id={`${idPrefix}-section-${key}`}
                type="checkbox"
                checked={variant.input_toggles.includes(key)}
                onChange={() => toggleSection(key)}
              />
              {docs?.[key]?.title || key}
            </label>
          ))}
        </SectionChecks>
      </TuningFieldFull>
      <TuningFieldFull>
        <TokenSummaryBox rows={tokenRows} />
        <GenerateButton type="button" disabled={runDisabled} onClick={onRun}>
          {running
            ? <><FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: "8px" }} />Starting run...</>
            : runLabel}
        </GenerateButton>
        {stale && <StaleNote>Inputs have changed since the last run — click Re-run to refresh.</StaleNote>}
      </TuningFieldFull>
      <VariantActions>
        <TuningSecondaryBtn type="button" onClick={onExport}>Export variant {variant.label}</TuningSecondaryBtn>
        <TuningSecondaryBtn type="button" disabled={!onDuplicate} onClick={onDuplicate}>
          Duplicate variant {variant.label}
        </TuningSecondaryBtn>
        {onRemove && (
          <TuningSecondaryBtn type="button" onClick={onRemove}>Remove variant {variant.label}</TuningSecondaryBtn>
        )}
      </VariantActions>
    </LlmTuningPanel>
  );
};

const VariantActions = styled.div`
  display: flex;
  gap: 8px;
`;

const SectionChecks = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px 20px;
  font-family: "Inter", sans-serif;
  font-size: 13px;
  color: ${colors.nightlyWoods500};

  label {
    display: flex;
    align-items: center;
    gap: 6px;
    margin: 0;
    cursor: pointer;
  }
`;

export const Card = styled.section`
  background: white;
  border-radius: 12px;
  box-shadow: 0px 1px 3px rgba(14, 26, 49, 0.1), 0px 1px 2px rgba(14, 26, 49, 0.06);
  padding: 16px;
  margin: 0 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  font-family: "Inter", sans-serif;
  color: ${colors.nightlyWoods500};
`;

export const SectionTitle = styled.h3`
  font-family: "Inter", sans-serif;
  font-size: 16px;
  font-weight: 700;
  line-height: 22px;
  letter-spacing: 0.25px;
  color: ${colors.nightlyWoods500};
  margin: 8px 0 4px;
`;

export const Row = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
`;

export const Muted = styled.span`
  color: ${colors.milkGlass950};
  font-family: "Inter", sans-serif;
  font-size: 12px;
`;

export const LinkButton = styled.button`
  background: none;
  border: none;
  color: ${colors.blue500};
  font-family: "Inter", sans-serif;
  font-size: 14px;
  font-weight: 500;
  padding: 0;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const ErrorText = styled.div`
  padding: 12px 16px;
  background: ${colors.orange50};
  color: ${colors.orange700};
  border: 1px solid ${colors.orange300};
  border-radius: 6px;
  font-family: "Inter", sans-serif;
  font-size: 14px;
  white-space: pre-wrap;
`;

export const Pre = styled.pre`
  margin: 0;
  padding: 12px;
  background: ${colors.milkGlass500};
  border: 1px solid ${colors.milkGlass600};
  border-radius: 6px;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 12px;
  line-height: 1.5;
  color: ${colors.nightlyWoods500};
  white-space: pre-wrap;
  word-break: break-word;
  max-height: 360px;
  overflow: auto;
`;

const DocBody = styled.span`
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 12px;
  font-weight: normal;
  white-space: normal;
  text-align: left;
`;

const Term = styled.span`
  border-bottom: 1px dotted currentColor;
  cursor: help;
`;

const SessionId = styled.span`
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 12px;
  white-space: nowrap;
  user-select: all;
`;

const CopyRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 8px 0;
`;

const Legend = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
`;

const ScrollX = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;

  th, td {
    border-top: 1px solid ${colors.milkGlass600};
    padding: 6px 8px;
    text-align: left;
    vertical-align: top;
  }
`;

const Grid = styled(Table)`
  th {
    font-weight: 600;
    white-space: nowrap;
  }
`;

const StateCell = styled.td`
  && {
    background: ${({ $bg }) => $bg};
    color: ${({ $fg }) => $fg};
    text-align: center;
    padding: 2px 8px;
    border-radius: 4px;
  }
`;

const Pane = styled.div`
  border: 1px solid #ccc4b8;
  border-radius: 6px;
`;

const PaneHeader = styled.h4`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin: 0;
  padding: 8px 12px;
  background-color: #f6f2ec;
  border-bottom: 1px solid #e3ddd4;
  font-size: 12px;

  div {
    display: flex;
    gap: 4px;
  }
`;

const ViewButton = styled(BootstrapButton)`
  font-size: 11px;
  padding: 2px 10px;
`;

const PaneBody = styled.div`
  padding: 12px;
`;

const RawOutput = styled.div`
  font-family: monospace;
  font-size: 11px;
  line-height: 1.5;
  white-space: pre-wrap;
`;

const Fold = styled.details`
  border-top: 1px solid #e3ddd4;
  padding-top: 8px;

  summary {
    cursor: pointer;
    font-weight: bold;
  }
`;

const FoldHint = styled.span`
  color: ${colors.nightlyWoods500};
  font-weight: normal;
`;

const FoldBody = styled.div`
  margin-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const MarkdownBody = styled.div`
  font-size: 14px;
  color: ${colors.nightlyWoods500};

  h1 { font-size: 20px; }
  h2 { font-size: 17px; }
  h3 { font-size: 15px; }

  table {
    border-collapse: collapse;
    margin: 8px 0;
  }

  th, td {
    border: 1px solid ${colors.milkGlass600};
    padding: 4px 8px;
    text-align: left;
    vertical-align: top;
  }
`;
